// 고정 60Hz 시뮬레이션 + 가변 렌더 루프. 게임 로직은 step()에서만 진행하고, 렌더는 alpha로 보간한다.
import { FIXED_DT } from '../shared/config';

export interface LoopHooks {
  /** 고정 스텝 1회(FIXED_DT) */
  step(): void;
  /** alpha: 이전→현재 상태 보간 비율 0..1, frameDt: 실제 경과 초 */
  render(alpha: number, frameDt: number): void;
}

/** 따라잡기 상한: 한 프레임 최대 1초·60스텝(1fps까지 실시간 유지) */
const CATCH_UP_FRAME_S = 1;
const CATCH_UP_STEPS = 60;

export class FixedLoop {
  private acc = 0;
  private last = -1;
  /** 지금까지 실행한 스텝 수 */
  steps = 0;
  /** 백로그 때문에 버린 스텝 수(느린 기기 진단용) */
  dropped = 0;
  /**
   * 따라잡기(파티 판처럼 서버가 실시간으로 도는 판, 레인 D): 느린 기기에서 프레임당 스텝·프레임 길이 상한을 넓혀
   * 내 영웅 시뮬이 실시간보다 늦어지지 않게 한다(서버 몹·동료는 실시간이다). 혼자 하는 판은 지금처럼 늦춰 그린다
   */
  catchUp = false;

  constructor(
    private readonly hooks: LoopHooks,
    private readonly maxStepsPerFrame = 6,
  ) {}

  /** rAF 타임스탬프(ms)로 한 프레임 진행. 테스트에서는 가짜 시계로 호출한다. */
  advance(nowMs: number): void {
    if (this.last < 0) this.last = nowMs;
    let frame = (nowMs - this.last) / 1000;
    this.last = nowMs;
    if (frame < 0) frame = 0;
    const cap = this.catchUp ? CATCH_UP_FRAME_S : 0.25;
    if (frame > cap) frame = cap;
    this.acc += frame;
    let n = 0;
    const max = this.catchUp ? Math.max(this.maxStepsPerFrame, CATCH_UP_STEPS) : this.maxStepsPerFrame;
    while (this.acc >= FIXED_DT && n < max) {
      this.hooks.step();
      this.acc -= FIXED_DT;
      this.steps++;
      n++;
    }
    if (this.acc >= FIXED_DT) {
      const extra = Math.floor(this.acc / FIXED_DT);
      this.dropped += extra;
      this.acc -= extra * FIXED_DT;
    }
    this.hooks.render(this.acc / FIXED_DT, frame);
  }

  /** 탭 복귀 등으로 시간 기준을 다시 잡는다(밀린 시간을 한꺼번에 돌리지 않는다). */
  resetClock(): void {
    this.last = -1;
    this.acc = 0;
  }
}

export function runLoop(loop: FixedLoop): () => void {
  let id = 0;
  const tick = (t: number) => {
    loop.advance(t);
    id = requestAnimationFrame(tick);
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') loop.resetClock();
  };
  document.addEventListener('visibilitychange', onVisibility);
  id = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(id);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
